import { useEffect, useState, useRef, useCallback, useLayoutEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Parallax } from "react-scroll-parallax";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { productPath, pathFor } from "../app/paths";
import { TextMarquee } from "../components/TextMarquee";
import { translatedList, type StatItem } from "../i18n/translatedList";

gsap.registerPlugin(ScrollTrigger);

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

interface CollectionItem {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  card_image_url: string | null;
  min_price_cents: number | null;
}

interface CollectionProduct {
  id: string;
  title: string;
  slug: string;
  price_cents: number;
  media: { id: string; type: string; url: string }[];
}

interface HeroSlide {
  id: string;
  image_url: string;
  title: string | null;
  subtitle: string | null;
  sort_order: number;
}

/* ─── Collection card with 3D Y-axis flip ─── */
function CollectionCard({
  col,
  index,
  locale,
  cfg,
  t,
  isMobile,
}: {
  col: CollectionItem;
  index: number;
  locale: string;
  cfg: ReturnType<typeof useSiteConfig>;
  t: (k: string) => string;
  isMobile: boolean;
}) {
  const [flipped, setFlipped] = useState(false);
  const [products, setProducts] = useState<CollectionProduct[]>([]);
  const [loadedProducts, setLoadedProducts] = useState(false);

  const handleEnter = () => {
    if (isMobile) return;
    setFlipped(true);
    if (!loadedProducts) {
      fetch(`${API_BASE}/api/v1/shop/collections/${col.slug}?lang=it`)
        .then((r) => r.json())
        .then((data: { products: CollectionProduct[] }) => {
          setProducts(data.products || []);
          setLoadedProducts(true);
        })
        .catch(() => {});
    }
  };

  const handleLeave = () => {
    if (isMobile) return;
    setFlipped(false);
  };

  const fmt = (cents: number) => (cents / 100).toFixed(2).replace(".", ",");

  /* Faccia frontale della scheda — condivisa tra desktop (flip) e mobile (link diretto). */
  const frontInner = (
    <div style={{ width: "100%", height: "100%", background: cfg.color_light_bg, position: "relative" }}>
      {col.image_url && (
        <img
          src={resolveImageUrl(col.image_url)}
          alt={col.title}
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%", display: "block" }}
        />
      )}
      {/* Gradient */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "55%", background: "linear-gradient(transparent, rgba(0,0,0,0.7))", pointerEvents: "none" }} />
      {/* Text */}
      <div style={{ position: "absolute", bottom: 20, left: 20, right: 20 }}>
        <p style={{ fontSize: 10, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase" as const, color: "#fff", margin: "0 0 6px" }}>
          {String(index + 1).padStart(2, "0")}
        </p>
        <h3 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 17 : 20, fontWeight: 400, color: "#fff", margin: 0 }}>
          {col.title}
        </h3>
      </div>
    </div>
  );

  /* P1.1 — Su mobile la scheda NON si gira (niente hover al tocco): la card è un
     link diretto alla collezione. Mostriamo un button "Scopri" BEN VISIBILE e
     cliccabile sotto il titolo, così l'azione è chiara e raggiungibile col tap. */
  if (isMobile) {
    return (
      <Link
        // Alla sezione della collezione, non solo alla pagina: chi tocca
        // «Città» si aspetta di vedere Città, non l'inizio di un elenco in cui
        // cercarla.
        to={`${pathFor("collections", locale)}#${col.slug}`}
        aria-label={col.title}
        style={{ display: "block", textDecoration: "none", color: "inherit" }}
      >
        <div style={{ position: "relative", aspectRatio: "4/5", overflow: "hidden", background: cfg.color_light_bg }}>
          {col.image_url && (
            <img
              src={resolveImageUrl(col.image_url)}
              alt={col.title}
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%", display: "block" }}
            />
          )}
          {/* Gradient (più marcato in basso per far risaltare titolo + button) */}
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "62%", background: "linear-gradient(transparent, rgba(0,0,0,0.78))", pointerEvents: "none" }} />
          {/* Testo (sinistra) + CTA "Scopri" (basso a destra) — layout flex */}
          <div style={{
            position: "absolute",
            bottom: 16,
            left: 16,
            right: 16,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 12,
          }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 10, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase" as const, color: "#fff", margin: "0 0 6px" }}>
                {String(index + 1).padStart(2, "0")}
              </p>
              <h3 style={{ fontFamily: cfg.font_heading, fontSize: 17, fontWeight: 400, color: "#fff", margin: 0, lineHeight: 1.15 }}>
                {col.title}
              </h3>
            </div>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                flexShrink: 0,
                padding: "10px 18px",
                background: cfg.color_accent,
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 1.2,
                textTransform: "uppercase" as const,
                borderRadius: 24,
                boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
              }}
            >
              {t("collectionDetail.viewDetails")}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{ perspective: 1000, cursor: "pointer" }}
    >
      <div
        style={{
          position: "relative",
          aspectRatio: "4/5",
          transformStyle: "preserve-3d",
          transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* ── FRONT ── */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            overflow: "hidden",
          }}
        >
          {frontInner}
        </div>

        {/* ── BACK ── */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            background: cfg.color_dark,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Mini hero with second image */}
          <div style={{ position: "relative", height: "50%", overflow: "hidden", flexShrink: 0 }}>
            {(col.card_image_url || col.image_url) && (
              <img
                src={resolveImageUrl(col.card_image_url || col.image_url)}
                // La card di una collezione è contenuto, non decorazione: senza
                // descrizione un motore vede un'immagine muta al posto di
                // «Borghi», «Città», «Momenti».
                alt={col.title}
                loading="lazy"
                decoding="async"
                width={800}
                height={1000}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            )}
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 100%)" }} />
            <div style={{ position: "absolute", bottom: 12, left: 16, right: 16 }}>
              <p style={{ fontSize: 8, fontWeight: 600, letterSpacing: 2.5, textTransform: "uppercase", color: cfg.color_accent, margin: "0 0 4px" }}>
                {String(index + 1).padStart(2, "0")} / {t("collectionsIndex.giftBox")}
              </p>
              <h3 style={{ fontFamily: cfg.font_heading, fontSize: 17, fontWeight: 400, color: "#fff", margin: 0, lineHeight: 1.15 }}>
                {col.title}
              </h3>
            </div>
          </div>

          {/* Product list — compact */}
          <div style={{ flex: 1, overflowY: "auto", padding: "6px 0 12px" }}>
            {products.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <div style={{ width: 16, height: 16, border: "1.5px solid rgba(255,255,255,0.1)", borderTopColor: cfg.color_accent, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
              </div>
            ) : (
              products.map((p, pi) => {
                const thumb = p.media.find((m) => m.type === "image");
                return (
                  <Link
                    key={p.id}
                    to={productPath(locale, p.slug)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "7px 16px", textDecoration: "none", color: "inherit",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ width: 36, height: 36, flexShrink: 0, overflow: "hidden", background: "rgba(255,255,255,0.04)", borderRadius: 3, position: "relative" }}>
                      {thumb ? (
                        <img src={resolveImageUrl(thumb.url)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      ) : (
                        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <div style={{ width: 4, height: 4, background: "rgba(255,255,255,0.15)" }} />
                        </div>
                      )}
                      <span style={{ position: "absolute", bottom: 1, right: 1, fontSize: 7, fontWeight: 700, color: "#fff", background: "rgba(0,0,0,0.6)", padding: "0 3px", lineHeight: 1.4 }}>
                        {String(pi + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 11, fontWeight: 500, color: "#fff", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</p>
                      <p style={{ fontSize: 10, color: "#fff", margin: 0 }}>{fmt(p.price_cents)} €</p>
                    </div>
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2.5"><path d="M9 18l6-6-6-6" /></svg>
                  </Link>
                );
              })
            )}
          </div>

          {/* CTA — compact */}
          <div style={{ padding: "10px 16px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <Link
              to={`${pathFor("collections", locale)}#${col.slug}`}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "9px", background: cfg.color_accent, color: "#fff",
                textDecoration: "none", fontSize: 9, fontWeight: 600, letterSpacing: 1.5,
                textTransform: "uppercase", transition: "opacity 0.2s", borderRadius: 3,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
            >
              {t("collectionDetail.viewDetails")}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Counter ─── */
function Counter({ to, suffix = "" }: { to: number; suffix?: string }) {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          obs.disconnect();
          const obj = { v: 0 };
          gsap.to(obj, { v: to, duration: 1.8, ease: "power2.out", onUpdate: () => setVal(Math.round(obj.v)) });
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

export function Home() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const isMobile = useIsMobile();
  // Contenuti presi dalle traduzioni: si aggiornano da soli al cambio lingua,
  // perché `t` cambia identità e il componente si ridisegna.
  const stats = translatedList<StatItem>(t, "home.stats.items");
  const [collections, setCollections] = useState<CollectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const heroRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/shop/collections?lang=${locale || "it"}`)
      .then((r) => r.json())
      .then(setCollections)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [locale]);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/shop/hero-slides?lang=${locale || "it"}`)
      .then((r) => r.json())
      .then(setHeroSlides)
      .catch(() => {});
  }, [locale]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (heroSlides.length > 0 ? (prev + 1) % heroSlides.length : 0));
  }, [heroSlides.length]);

  useEffect(() => {
    if (heroSlides.length <= 1) return;
    timerRef.current = setInterval(nextSlide, 6000);
    return () => clearInterval(timerRef.current);
  }, [heroSlides.length, nextSlide]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(nextSlide, 6000);
  };

  /* ─── Hero GSAP entrance per slide ─── */
  useLayoutEffect(() => {
    const el = heroRef.current;
    if (!el || !el.children.length) return;
    const tl = gsap.timeline();
    gsap.set(el.children, { y: 70, opacity: 0 });
    tl.to(el.children, {
      y: 0,
      opacity: 1,
      duration: 1.2,
      stagger: 0.18,
      ease: "expo.out",
      delay: 0.2,
    });
    return () => { tl.kill(); };
  }, [currentSlide, heroSlides.length]);

  /* ─── GSAP ScrollTrigger animations ─── */
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const ctx = gsap.context(() => {
      /* Section headers — clip reveal */
      gsap.utils.toArray<HTMLElement>(".gs-header").forEach((el) => {
        gsap.from(el, {
          y: 60,
          opacity: 0,
          duration: 1.1,
          ease: "expo.out",
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });

      /* Stagger children */
      gsap.utils.toArray<HTMLElement>(".gs-stagger").forEach((el) => {
        gsap.from(el.children, {
          y: 70,
          opacity: 0,
          duration: 1,
          stagger: 0.12,
          ease: "expo.out",
          scrollTrigger: { trigger: el, start: "top 82%", once: true },
        });
      });

      /* Slide from left */
      gsap.utils.toArray<HTMLElement>(".gs-from-left").forEach((el) => {
        gsap.from(el, {
          x: -80,
          opacity: 0,
          duration: 1.3,
          ease: "expo.out",
          scrollTrigger: { trigger: el, start: "top 82%", once: true },
        });
      });

      /* Slide from right */
      gsap.utils.toArray<HTMLElement>(".gs-from-right").forEach((el) => {
        gsap.from(el, {
          x: 80,
          opacity: 0,
          duration: 1.3,
          ease: "expo.out",
          scrollTrigger: { trigger: el, start: "top 82%", once: true },
        });
      });

      /* Scale up reveal */
      gsap.utils.toArray<HTMLElement>(".gs-scale").forEach((el) => {
        gsap.from(el, {
          scale: 0.92,
          opacity: 0,
          duration: 1.2,
          ease: "expo.out",
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });

      /* Animated lines */
      gsap.utils.toArray<HTMLElement>(".gs-line").forEach((el) => {
        gsap.from(el, {
          width: 0,
          duration: 1.4,
          ease: "expo.out",
          scrollTrigger: { trigger: el, start: "top 90%", once: true },
        });
      });

      /* Stats counters container */
      gsap.utils.toArray<HTMLElement>(".gs-stats").forEach((el) => {
        gsap.from(el.children, {
          y: 40,
          opacity: 0,
          duration: 0.9,
          stagger: 0.08,
          ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });

      /* Product cards with rotation hint */
      gsap.utils.toArray<HTMLElement>(".gs-product-card").forEach((el, i) => {
        gsap.from(el, {
          y: 80,
          opacity: 0,
          rotateX: 8,
          duration: 1.1,
          delay: i * 0.15,
          ease: "expo.out",
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });

      /* CTA button */
      gsap.utils.toArray<HTMLElement>(".gs-cta").forEach((el) => {
        gsap.from(el, {
          y: 30,
          opacity: 0,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 90%", once: true },
        });
      });

    }, containerRef);

    return () => ctx.revert();
  }, [loading, collections.length]);


  return (
    <div ref={containerRef} style={{ overflow: "hidden" }}>
      {/* ═══════════════════════════════════════════════
          HERO — 70vh
      ═══════════════════════════════════════════════ */}
      <section
        style={{
          background: cfg.color_dark,
          position: "relative",
          overflow: "hidden",
          height: "70vh",
          maxHeight: isMobile ? 480 : 640,
          minHeight: isMobile ? 360 : 440,
        }}
      >
        {heroSlides.length > 0 ? (
          <>
            <Parallax speed={isMobile ? -8 : -20} style={{ position: "absolute", inset: "-25% 0", height: "150%" }}>
              {heroSlides.map((slide, i) => (
                <div
                  key={slide.id}
                  style={{
                    position: "absolute",
                    inset: 0,
                    opacity: i === currentSlide ? 1 : 0,
                    transition: "opacity 1.6s cubic-bezier(0.4,0,0.2,1)",
                  }}
                >
                  <img
                    src={resolveImageUrl(slide.image_url)}
                    // La slide hero è ciò che riempie lo schermo all'apertura:
                    // per un motore è il contenuto principale della pagina, e
                    // `alt=""` la dichiarava decorativa. Il titolo della slide
                    // arriva dal CMS ed è la descrizione giusta.
                    alt={slide.title || cfg.brand_name}
                    // È l'immagine più grande della prima schermata, cioè
                    // quella su cui si misura l'LCP: dirlo al browser le fa
                    // saltare la coda delle altre risorse.
                    fetchPriority={i === 0 ? "high" : "low"}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                    // Dimensioni esplicite: senza, il browser non sa quanto
                    // spazio riservare e la pagina sobbalza quando l'immagine
                    // arriva. È metà del punteggio di stabilità visiva.
                    width={1920}
                    height={1080}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ))}
            </Parallax>

            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 20%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.72) 100%)", zIndex: 1 }} />

            <div
              ref={heroRef}
              key={`hero-${currentSlide}`}
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 2,
                maxWidth: 1200,
                margin: "0 auto",
                padding: isMobile ? "0 24px 40px" : "0 48px 56px",
              }}
            >
              {/* Filetto editoriale: struttura senza aggiungere testo. È anche
                  il primo elemento dello stagger GSAP (anima con i figli di heroRef). */}
              <div style={{ width: isMobile ? 36 : 48, height: 1, background: "rgba(255,255,255,0.5)", margin: isMobile ? "0 0 16px" : "0 0 22px" }} />

              {heroSlides[currentSlide]?.title && (
                <h1
                  style={{
                    fontFamily: cfg.font_heading,
                    /* Scala FLUIDA leggermente ridotta (28px → 68px, prima 36px → 76px):
                       serve a far stare il titolo su UNA riga anche sui viewport stretti,
                       dove il vecchio minimo di 36px arrivava a filo del contenitore. */
                    fontSize: "clamp(1.75rem, 5.4vw, 4.25rem)",
                    fontWeight: 400,
                    color: "#fff",
                    lineHeight: 1.04,
                    letterSpacing: "-0.02em",
                    /* Larghezza piena del contenitore: il vecchio cap di 15ch era ciò che
                       mandava a capo il titolo pur avendo spazio libero a destra. */
                    maxWidth: "100%",
                    /* Il titolo non va MAI a capo (il sottotitolo sì, resta invariato). */
                    whiteSpace: "nowrap" as const,
                    margin: "0 0 clamp(14px, 1.6vw, 22px)",
                    textShadow: "0 2px 24px rgba(0,0,0,0.30)",
                  }}
                >
                  {heroSlides[currentSlide].title}
                </h1>
              )}
              {heroSlides[currentSlide]?.subtitle && (
                <p
                  style={{
                    fontSize: "clamp(0.9375rem, 1.2vw, 1.125rem)",
                    /* Bianco attenuato: crea gerarchia col titolo invece di competerci. */
                    color: "rgba(255,255,255,0.82)",
                    maxWidth: "min(100%, 42ch)",
                    margin: "0 0 clamp(26px, 3vw, 36px)",
                    lineHeight: 1.55,
                    fontWeight: 400,
                    textShadow: "0 1px 12px rgba(0,0,0,0.32)",
                  }}
                >
                  {heroSlides[currentSlide].subtitle}
                </p>
              )}
              <div>
                <Link
                  to={pathFor("collections", locale)}
                  className="hero-cta"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 12,
                    /* Da link testuale 11px a bottone vero: contrasto pieno sulla foto
                       e area di tocco ≥48px (prima ~23px, sotto le linee guida). */
                    background: "#fff",
                    color: cfg.color_dark,
                    textDecoration: "none",
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 1.6,
                    textTransform: "uppercase" as const,
                    padding: isMobile ? "15px 26px" : "17px 34px",
                    minHeight: 48,
                    borderRadius: 2,
                    boxShadow: "0 6px 28px rgba(0,0,0,0.22)",
                  }}
                >
                  {t("collections.title")}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>

            {heroSlides.length > 1 && !isMobile && (
              <div style={{ position: "absolute", left: 48, top: "50%", transform: "translateY(-50%)", zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                {heroSlides.map((_, i) => (
                  <button key={i} onClick={() => goToSlide(i)} style={{ width: 2, height: i === currentSlide ? 36 : 10, borderRadius: 1, border: "none", background: i === currentSlide ? "#fff" : "rgba(255,255,255,0.2)", cursor: "pointer", transition: "all 0.6s cubic-bezier(0.16,1,0.3,1)", padding: 0 }} aria-label={t("a11y.heroSlide", { n: i + 1, total: heroSlides.length })} />
                ))}
              </div>
            )}

            {heroSlides.length > 1 && (
              <div style={{ position: "absolute", bottom: isMobile ? 16 : 24, right: isMobile ? 24 : 48, zIndex: 10, display: "flex", alignItems: "baseline", gap: 3 }}>
                <span key={currentSlide} style={{ fontSize: 22, fontWeight: 400, fontFamily: cfg.font_heading, color: "#fff", lineHeight: 1 }}>
                  {String(currentSlide + 1).padStart(2, "0")}
                </span>
                <span style={{ fontSize: 11, color: "#fff", fontWeight: 500, letterSpacing: 1 }}>
                  &thinsp;/&thinsp;{String(heroSlides.length).padStart(2, "0")}
                </span>
              </div>
            )}

            {heroSlides.length > 1 && isMobile && (
              <div style={{ position: "absolute", bottom: 16, left: 24, zIndex: 10, display: "flex", gap: 5 }}>
                {heroSlides.map((_, i) => (
                  <button key={i} onClick={() => goToSlide(i)} style={{ width: i === currentSlide ? 22 : 6, height: 2, borderRadius: 1, border: "none", background: i === currentSlide ? "#fff" : "rgba(255,255,255,0.2)", cursor: "pointer", transition: "all 0.4s ease", padding: 0 }} aria-label={t("a11y.heroSlide", { n: i + 1, total: heroSlides.length })} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "0 24px" : "0 48px", height: "100%", display: "flex", alignItems: "flex-end", paddingBottom: 56 }}>
            <div>
              <div style={{ width: isMobile ? 36 : 48, height: 1, background: "rgba(255,255,255,0.5)", margin: isMobile ? "0 0 16px" : "0 0 22px" }} />
              <h1 style={{ fontFamily: cfg.font_heading, fontSize: "clamp(2.25rem, 6.2vw, 4.75rem)", fontWeight: 400, color: "#fff", lineHeight: 1.04, letterSpacing: "-0.02em", maxWidth: "min(100%, 15ch)", textWrap: "balance" as const, margin: "0 0 clamp(20px, 2.4vw, 30px)", textShadow: "0 2px 24px rgba(0,0,0,0.30)" }}>{t("hero.title")}</h1>
              <Link
                to={pathFor("collections", locale)}
                className="hero-cta"
                style={{ display: "inline-flex", alignItems: "center", gap: 12, background: "#fff", color: cfg.color_dark, textDecoration: "none", fontSize: 12, fontWeight: 600, letterSpacing: 1.6, textTransform: "uppercase" as const, padding: isMobile ? "15px 26px" : "17px 34px", minHeight: 48, borderRadius: 2, boxShadow: "0 6px 28px rgba(0,0,0,0.22)" }}
              >
                {t("collections.title")}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* ═══ MARQUEE ═══
          Voci e separatore arrivano dai file di traduzione: per aggiungerne una,
          toglierla o cambiarne l'ordine si modifica solo `home.marquee.items`. */}
      <TextMarquee
        items={translatedList<string>(t, "home.marquee.items")}
        ariaLabel={t("home.marquee.ariaLabel")}
        separator={t("home.marquee.separator")}
        background={cfg.color_dark}
      />

      {/* ═══ COLLECTIONS ═══ */}
      <section style={{ background: cfg.color_body_bg, padding: isMobile ? "72px 24px" : "120px 48px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="gs-header" style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "flex-end", marginBottom: isMobile ? 36 : 56, flexDirection: isMobile ? "column" : "row", gap: 16 }}>
            <div>
              <div className="gs-line" style={{ width: 48, height: 1, background: cfg.color_muted, marginBottom: 20 }} />
              <p style={{ fontSize: 11, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase" as const, color: cfg.color_muted, margin: "0 0 12px" }}>{t("collections.title")}</p>
              <h2 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 28 : 40, fontWeight: 400, color: cfg.color_dark, margin: 0, lineHeight: 1.12, letterSpacing: -0.4 }}>{t("hc.title")}</h2>
            </div>
            <Link to={pathFor("collections", locale)} style={{ fontSize: 12, fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase" as const, color: cfg.color_dark, textDecoration: "none", borderBottom: `1px solid ${cfg.color_dark}`, paddingBottom: 5, transition: "opacity 0.3s", whiteSpace: "nowrap" }} onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.5"; }} onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}>
              {t("nav.collections")}
            </Link>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 80 }}>
              <div style={{ width: 24, height: 24, border: "2px solid rgba(28,75,59,0.89)", borderTopColor: cfg.color_primary, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
            </div>
          ) : collections.length > 0 ? (
            <div className="gs-stagger" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${Math.min(collections.length, 3)}, 1fr)`, gap: isMobile ? 20 : 24 }}>
              {collections.map((col, i) => (
                <CollectionCard
                  key={col.id}
                  col={col}
                  index={i}
                  locale={locale || "it"}
                  cfg={cfg}
                  t={t}
                  isMobile={isMobile}
                />
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {/* ═══ STATS ═══
          Numero, suffisso ed etichetta vivono insieme nelle traduzioni: sono
          contenuto (li decide il marketing), non costanti tecniche. La griglia
          si adatta al numero di voci, così aggiungerne o toglierne una non
          richiede di toccare il layout. */}
      <section style={{ background: cfg.color_body_bg, borderTop: "1px solid rgba(28,75,59,0.89)", borderBottom: "1px solid rgba(28,75,59,0.89)" }}>
        <div className="gs-stats" style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "44px 24px" : "56px 48px", display: "grid", gridTemplateColumns: `repeat(${Math.max(stats.length, 1)}, 1fr)`, gap: isMobile ? 16 : 0 }}>
          {stats.map((stat, i, arr) => (
            <div key={i} style={{ textAlign: "center", borderRight: !isMobile && i < arr.length - 1 ? "1px solid rgba(28,75,59,0.89)" : "none" }}>
              <div style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 32 : 44, fontWeight: 400, color: cfg.color_dark, lineHeight: 1, marginBottom: 6, letterSpacing: -1 }}>
                <Counter to={stat.value} suffix={stat.suffix} />
              </div>
              <p style={{ fontSize: 10, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase" as const, color: cfg.color_muted, margin: 0 }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <style>{`
        @keyframes kenBurns {
          0% { transform: scale(1); }
          100% { transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
}
